import { useEffect, useState } from "react";
import {
  ApiError,
  api,
  type Contacto,
  type ContactoCreate,
  type ContactoUpdate,
} from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";
import { ToggleTrack } from "@/ui/Toggle";

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  clienteId: number;
  clienteNombre: string;
  // null = crear nuevo contacto en este cliente.
  // Contacto = editar el existente.
  contacto: Contacto | null;
};

/** Modal compacto para crear o editar un contacto del cliente.
 *
 * Modo crear (contacto=null): POST /api/clientes/{id}/contactos
 * Modo editar (contacto=Contacto): PATCH /api/clientes/{id}/contactos/{id}
 *
 * El email NO es identidad — se permite vacío (a veces el cliente no nos
 * lo da) y se permite duplicado (la identidad es el id del sistema).
 */
export default function EditarContactoModal({
  open,
  onClose,
  onSaved,
  clienteId,
  clienteNombre,
  contacto,
}: Props) {
  const toast = useToast();
  const isEdit = contacto !== null;

  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [principal, setPrincipal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Re-sincronizar al abrir o cambiar de contacto editado.
  useEffect(() => {
    if (!open) return;
    setNombre(contacto?.nombre ?? "");
    setEmail(contacto?.email ?? "");
    setTelefono(contacto?.telefono ?? "");
    setPrincipal(contacto?.principal ?? false);
  }, [open, contacto]);

  async function guardar() {
    const nombreTrim = nombre.trim();
    if (!nombreTrim) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      if (isEdit && contacto) {
        const payload: ContactoUpdate = {
          nombre: nombreTrim,
          email: email.trim() || null,
          telefono: telefono.trim() || null,
          principal,
        };
        await api.patch<Contacto>(
          `/api/clientes/${clienteId}/contactos/${contacto.id}`,
          payload,
        );
        toast.success("Contacto actualizado", nombreTrim);
      } else {
        const payload: ContactoCreate = {
          nombre: nombreTrim,
          email: email.trim() || null,
          telefono: telefono.trim() || null,
        };
        const nuevo = await api.post<Contacto>(
          `/api/clientes/${clienteId}/contactos`,
          payload,
        );
        // Si marcaron "principal" al crear, hacemos un PATCH separado para
        // demarcar a los demas — el POST no lo expone (campo derivado).
        if (principal) {
          await api.patch<Contacto>(
            `/api/clientes/${clienteId}/contactos/${nuevo.id}`,
            { principal: true },
          );
        }
        toast.success("Contacto creado", nombreTrim);
      }
      onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error(isEdit ? "No pude actualizar" : "No pude crear", msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={isEdit ? `Editar contacto · ${clienteNombre}` : `Nuevo contacto · ${clienteNombre}`}
      description={
        isEdit
          ? "Los cambios se reflejan en todos los casos que apuntan a este contacto."
          : "Registra una persona del lado del cliente. El correo es opcional."
      }
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-ghost" disabled={saving}>
            Cancelar
          </button>
          <button
            onClick={guardar}
            disabled={saving || !nombre.trim()}
            className="btn-primary disabled:opacity-50"
          >
            {saving ? "Guardando…" : isEdit ? "Guardar" : "Crear"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {isEdit && contacto && !contacto.activo && (
          <div className="rounded border border-zinc-600 bg-zinc-700/30 px-3 py-2 text-xs text-zinc-300">
            Este contacto está <strong>archivado</strong>. Sigue visible en
            los casos viejos que lo apuntaban; reactívalo desde la lista si
            quieres volver a usarlo en casos nuevos.
          </div>
        )}
        <div>
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            Nombre *
          </label>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="input mt-1 w-full"
            autoFocus
            placeholder="Ej. Juan Pérez"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400">
              Correo
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input mt-1 w-full"
              placeholder="opcional"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400">
              Teléfono
            </label>
            <input
              type="text"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              className="input mt-1 w-full"
              placeholder="opcional"
            />
          </div>
        </div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="sr-only"
            checked={principal}
            onChange={(e) => setPrincipal(e.target.checked)}
          />
          <ToggleTrack checked={principal} className="mt-0.5" />
          <span className="text-sm text-zinc-300">
            Marcar como contacto principal
            <span className="block text-xs text-zinc-500">
              Si activas esto, los otros contactos de {clienteNombre} dejan de
              ser principales (solo uno a la vez).
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
